/**
 * The app's axios interceptors: the 401 → refresh → retry lifecycle, and the platform header.
 *
 * Both used to live at the top level of `src/commonjs/api.ts`, registering as a side effect of
 * importing a 2,000-line API client that seven call sites used. That is the only load-bearing part
 * of that file, so it moves here and the client can go.
 *
 * `registerAuthInterceptors()` must be called exactly once, at app entry, **before the first
 * request**. It is idempotent — a second call is a no-op — because the cost of double registration
 * is two refreshes racing for a single-use refresh token, which logs the user out.
 *
 * Registered on the global axios instance, matching what `commonjs/api.ts` did. Moving both halves
 * onto a dedicated instance is A5; doing it here would change behaviour for every module in the app
 * in the same commit that moves the refresh lifecycle, which is one risk too many.
 *
 * `API_URL` comes from `@/constants/config`, **not** `@/modules/shared/constants`. The two are
 * different values: config's falls back to a hostname rewrite when `window.env` is absent, the
 * other resolves to `''`. `apiEndpoints` used config's, so the refresh endpoint keeps resolving the
 * way it does today. See §5.2 of `docs/ops-merge-context.md`.
 */
import axios from 'axios';
import { createBrowserHistory } from 'history';

import { API_URL, CLIENT_ID, CLIENT_SECRET } from '@/constants/config';
import {
  deleteLocalData,
  getAccessToken,
  getRefreshToken,
  removeAxiosHeaders,
  setAccessToken,
  setAxiosHeaders,
  setRefreshToken,
} from './hooks/useTokenManager';

/**
 * The refresh grant's URL. Exported because ops's request interceptor has to recognise this exact
 * request and NOT staple an `Authorization` header onto it: the response interceptor below calls
 * `removeAxiosHeaders()` immediately before POSTing here, precisely so the grant goes out without a
 * dead bearer. Put the old one back and the refresh 401s, logging the user out instead of
 * recovering them.
 *
 * It used to recognise it by rebuilding `${API_URL}auth/token` independently in `apiPath.ts`, which
 * matched only by coincidence — change the shape or base of this URL and that comparison would
 * start failing silently. One constant, one definition.
 */
export const REFRESH_TOKEN_URL = `${API_URL}auth/token`;

const history = createBrowserHistory({ forceRefresh: true } as never);

let registered = false;
let isTokenrefreshing = false;
let retryReqestArray: Array<{ retry: (token: string) => void; reject: (reason?: unknown) => void }> = [];

function onTokenRefresh(authorisationToken: string) {
  // Drain before flushing — the array must not survive into the next refresh
  // cycle, or old callbacks re-fire and duplicate requests.
  const queued = retryReqestArray;
  retryReqestArray = [];
  queued.forEach((entry) => entry.retry(authorisationToken));
}

function onTokenRefreshFailed(refreshError: unknown) {
  // Reject queued requests so their callers settle — leaving them pending
  // strands anything awaiting the response (e.g. loading toasts).
  const queued = retryReqestArray;
  retryReqestArray = [];
  queued.forEach((entry) => entry.reject(refreshError));
}

/** Name of the cross-tab lock guarding the refresh grant. Shared by every tab of this origin. */
const TOKEN_REFRESH_LOCK = 'vouch:token-refresh';

/**
 * Run `fn` holding a lock shared across every tab of this origin.
 *
 * `isTokenrefreshing` only serialises refreshes *within* one tab — it is a module global and each
 * tab has its own module instance. Two tabs therefore both read the same refresh token out of the
 * shared localStorage and both POST the refresh grant. The backend runs with ROTATE_REFRESH_TOKEN
 * on and REFRESH_TOKEN_GRACE_PERIOD_SECONDS at 0, so the first call rotates the token and the
 * second is rejected with invalid_grant. Web Locks are the only same-origin primitive that spans
 * tabs, so the refresh runs under one.
 *
 * This is not hypothetical, and it is this module's own flow: "See Quotation" opens the ops
 * dashboard in a new tab (`sameTab: false`), and the original tab's inbox-count query has
 * `refetchOnWindowFocus` enabled, so tabbing between them reliably fires two refreshes at once.
 *
 * Falls back to running `fn` directly where Web Locks are unavailable — the previous behaviour,
 * no worse, just unprotected.
 */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (navigator as unknown as { locks?: { request?: (name: string, cb: () => Promise<T>) => Promise<T> } })
    ?.locks;
  if (typeof locks?.request !== 'function') {
    return fn();
  }
  return locks.request(TOKEN_REFRESH_LOCK, fn);
}

export function registerAuthInterceptors(): void {
  if (registered) return;
  registered = true;

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error && error.response && error.response.status === 401) {
        // Gate on the refresh token, not the access token. Refreshing needs only the former, and
        // gating on the latter made a single failed refresh permanent: the failure path below used
        // to delete the access token, so no later 401 could ever reach this branch again. The
        // session then limped on the cookie until the next hard reload, and died there.
        const seenRefreshToken = getRefreshToken();

        if (seenRefreshToken) {
          if (!isTokenrefreshing) {
            // Set before acquiring the lock so this tab's other 401s queue below rather than pile
            // up waiting on a lock they would each then contend for.
            isTokenrefreshing = true;

            return withRefreshLock(async () => {
              try {
                // Another tab may have refreshed while we waited. localStorage is shared across
                // this origin, so its new pair is already here. Spending our own refresh now would
                // present a token that has already been rotated away, and the backend would reject
                // it — the exact failure this lock exists to prevent.
                const currentRefreshToken = getRefreshToken();
                const currentAccessToken = getAccessToken();
                if (currentRefreshToken && currentRefreshToken !== seenRefreshToken && currentAccessToken) {
                  setAxiosHeaders(currentAccessToken);
                  onTokenRefresh(currentAccessToken);
                  const queuedRequest = error.config;
                  queuedRequest.headers.Authorization = `Bearer ${currentAccessToken}`;
                  return axios(queuedRequest);
                }

                // Strip the dead bearer before the grant goes out; ops's request interceptor
                // recognises REFRESH_TOKEN_URL and leaves it alone for the same reason.
                removeAxiosHeaders();
                const response = await axios.post(REFRESH_TOKEN_URL, {
                  client_id: CLIENT_ID,
                  client_secret: CLIENT_SECRET,
                  grant_type: 'refresh_token',
                  refresh_token: currentRefreshToken || seenRefreshToken,
                });

                setRefreshToken(response.data.refresh_token);
                setAccessToken(response.data.access_token);
                setAxiosHeaders(response.data.access_token);
                onTokenRefresh(response.data.access_token);

                const originalRequest = error.config;
                originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
                return axios(originalRequest);
              } catch (refreshError) {
                console.error(
                  'Token refresh failed:',
                  (refreshError as { response?: { data?: unknown } })?.response?.data,
                );
                onTokenRefreshFailed(refreshError);

                // Clear BOTH. Deleting only the access token left a state nothing could act on: a
                // refresh token that still looked usable but had already been rotated away, behind
                // a guard that could never be satisfied again.
                removeAxiosHeaders();
                deleteLocalData('accessToken');
                deleteLocalData('refreshToken');
                history.push('/');
                return Promise.reject(refreshError);
              } finally {
                isTokenrefreshing = false;
              }
            });
          }

          return new Promise((resolve, reject) => {
            retryReqestArray.push({
              retry: (token) => {
                const originalRequest = error.config;
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(axios(originalRequest));
              },
              reject,
            });
          });
        }

        // No refresh token, so nothing is recoverable. Only tear down if we believed we had a
        // session: a 401 with neither token is an unauthenticated request (a failed login, say)
        // and redirecting there would bounce the user off the page they are logging in on.
        if (getAccessToken()) {
          removeAxiosHeaders();
          deleteLocalData('accessToken');
          history.push('/');
        }
      }
      return Promise.reject(error);
    },
  );

  axios.interceptors.request.use((request) => {
    let requestPlatform = 'web';
    try {
      if (mobileClient) {
        requestPlatform = 'app';
      }
    } catch (e) {
      if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        requestPlatform = 'm-web';
      }
    }
    request.headers['X-REQUEST-PLATFORM'] = requestPlatform;
    return request;
  });
}
